import type { QueryRequestData } from '@ricoh/ria.client.core/plugins/swr/request';
import type { DataSource } from '@ricoh/ria.client.core/types/component/data.type';
import type { AsyncFunc } from '@ricoh/ria.client.core/types/core/function.type';

import DateTime from '@ricoh/ria.client.core/plugins/moment';
import { isNotNull } from '@ricoh/ria.client.core/utils/data/object';
import { listTransform } from '@ricoh/ria.client.core/utils/mapper/list';

import type { Batch, Document } from '~/common/model/api/batch';
import type { WorkQueueListItem } from '~/common/model/api/work-queue';
import type { SaveBatchRequestData } from '~/services/server/api/batch';
import type { GetWorkQueueListRequestData } from '~/components/feature/work-queue';

import { PROCESS_VIEWABLE_BATCH_STATUSES } from '~/common/constants/feature/process/batch';

import type { CreateSaveBatchRequestDataMappers, CreateSaveBatchRequestDataOptions, ProcessBatchFormField } from './_process.type';

import { createBatchFieldsWithValue, createBatchFieldsWithValueFormField } from './_batch';
import { createDocumentFieldsWithValue, createDocumentFieldsWithValueFormField } from './_document';

/**
 * Create ProcessBatch FormField.
 *
 * @param {Batch | undefined} batch Batch.
 *
 * @returns {ProcessBatchFormField} ProcessBatch FormField.
 */
export function createProcessBatchFormField(batch?: Batch): ProcessBatchFormField {
  return {
    batch: {
      name: batch?.name ?? '',
      createdBy: batch?.createdBy?.name ?? batch?.createdBy?.email ?? '',
      createdAt: DateTime.nullable(batch?.createdAt)?.toDate() ?? null,
    },
    batchFields: batch?.fields ? createBatchFieldsWithValueFormField(batch.fields) : {},
    documentFields: batch?.documents.map((document) => createDocumentFieldsWithValueFormField(document.fields)) ?? [],
  };
}

/**
 * Create SaveBatch RequestData from Batch.
 *
 * @param {CreateSaveBatchRequestDataOptions} options Options.
 * @param {CreateSaveBatchRequestDataMappers} mappers Mappers.
 *
 * @returns {SaveBatchRequestData} SaveBatch RequestData.
 */
export function createSaveBatchRequestData(
  { stage, batch, submitData, priority, initialData, excludePages }: CreateSaveBatchRequestDataOptions,
  { transformPages, transformOCRField }: CreateSaveBatchRequestDataMappers,
): SaveBatchRequestData {
  const documentsOmitKeys: (keyof Document)[] = ['id', 'sourceRef', 'stamps'];
  if (stage === 'new' || stage === 'review') {
    documentsOmitKeys.push('fields');
  }

  const pageIdMapping: Record<string, string> = {};

  return {
    id: batch.id,
    status: batch.status,
    priority,

    fields: listTransform.toRecord(
      stage === 'confirmation'
        ? [] // * Confirmation stage can not edit data -> Do not send batch fields
        : createBatchFieldsWithValue(batch, submitData.batchFields),
    ),

    documents: listTransform.toRecordWithEditStatus(batch.documents, {
      initialList: initialData?.documents ?? [],
      omitKeys: documentsOmitKeys,
      transform: (document, index, { status }) => {
        const documentFields = createDocumentFieldsWithValue(document, submitData.documentFields[index] ?? {}, status !== 'delete');

        const pages = transformPages(
          pageIdMapping,
          // * Include excluded pages into first document
          index === 0 ? [...excludePages, ...document.pages] : document.pages,
        );

        return {
          ...document,
          type: document.type ?? '',
          pages,
          fields: listTransform.toRecord(documentFields),
          ocr: listTransform.toRecord(documentFields, {
            transform: (field) => transformOCRField(pageIdMapping, field),
          }),
        };
      },
    }),
  };
}

/**
 * Find next batch in work queue list.
 *
 * @param {string} batchId Current batch id.
 * @param {QueryRequestData} query Current query data.
 * @param {AsyncFunc<[queryRequestData: GetWorkQueueListRequestData['query']], DataSource<WorkQueueListItem>>} fetch Fetch function.
 *
 * @returns {Promise<WorkQueueListItem | undefined>} Next batch.
 */
export async function findNextBatch(
  batchId: string,
  query: QueryRequestData,
  fetch: AsyncFunc<[queryRequestData: GetWorkQueueListRequestData['query']], DataSource<WorkQueueListItem>>,
): Promise<WorkQueueListItem | undefined> {
  let currentSkip;
  let nextBatch;

  // * Apply query + priority asc, createdAt asc
  const currentQuery: QueryRequestData = {
    ...query,
    sort: [
      ...(query.sort?.filter((sort) => sort.field !== 'priority' && sort.field !== 'createdAt') ?? []),
      { field: 'priority', dir: 'asc' },
      { field: 'createdAt', dir: 'asc' },
    ],
  };

  // * await in 'while' loop for getting next batch is necessary
  /* eslint-disable no-await-in-loop */
  while (!nextBatch) {
    const { items, page, total } = await fetch(currentQuery);
    nextBatch = items.find(({ id, stage: stageBatch }) => id !== batchId && PROCESS_VIEWABLE_BATCH_STATUSES.includes(stageBatch));
    if (nextBatch) {
      break;
    }

    // * Next skip
    const skip = page.skip + page.take;

    // * Break if back to current skip (**)
    if (isNotNull(currentSkip) && skip >= currentSkip) {
      // * Back to first page
      currentQuery.page = {
        ...page,
        skip: 0,
      };

      break;
    }

    // * Goto next page if current page is not the last page
    if (skip < total) {
      currentQuery.page = {
        ...page,
        skip,
      };
    }
    // * Back to first page if current page is the last page
    else {
      // * Save current skip to prevent infinite loop (**)
      currentSkip = page.skip;

      currentQuery.page = {
        ...page,
        skip: 0,
      };
    }
  }
  /* eslint-enable no-await-in-loop */

  return nextBatch;
}
